import {
  Calendar,
  Clock,
  Copy,
  CreditCard,
  Navigation,
  RotateCcw,
  Truck,
  Undo2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import type { BookProduct } from '@/domains/mobile-web/constant/listBook.constant';
import {
  type CourierType,
  STATUS_COURIER,
} from '@/domains/mobile-web/constant/listStatusCourier.constant';
import { cn } from '@/lib/utils';
import Button from '@/shared/components/ui/Button';
import { useColorStore } from '@/store/colorStore';

export type TransactionStatus = CourierType;

export type CardProductTransactionVariant = 'full' | 'compact';

export interface TransactionItem {
  product: TransactionItemProduct & { id: string | number };
  quantity: number;
}

export interface TransactionShipping {
  courier: string;
  trackingNumber: string;
  note?: string;
}

export interface TransactionOrder {
  id: string;
  invoice: string;
  date: string;
  status: CourierType;
  /** Badge text instead of the status's own label (e.g. Dikembalikan shown as cancel) */
  statusLabel?: string;
  /** Transfer reported, awaiting admin confirmation: Bayar is disabled */
  awaitingConfirmation?: boolean;
  /** Bayar Sebelum ...; no banner without it */
  paymentDeadline?: string;
  items: TransactionItem[];
  totalPayment: number;
  shipping?: TransactionShipping;
}

interface CardProductTransactionProps {
  transaction: TransactionOrder;
  onPay?: (transaction: TransactionOrder) => void;
  onCancel?: (transaction: TransactionOrder) => void;
  onTrack?: (transaction: TransactionOrder) => void;
  onReceived?: (transaction: TransactionOrder) => void;
  onRefund?: (transaction: TransactionOrder) => void;
  onBuyAgain?: (transaction: TransactionOrder) => void;
  onCopy?: (value: string, transaction: TransactionOrder) => void;
  variant?: CardProductTransactionVariant;
  className?: string;
}

const BTN_BASE = 'h-10 flex-1 rounded-[8px] text-[16px]';
const BTN_PRIMARY = 'bg-text-blue-100 text-white hover:bg-text-blue-100/90';
const BTN_OUTLINE = 'border border-blue-cs-200 bg-white text-black hover:bg-[#F3F8FF]';
const BTN_DANGER =
  'border border-text-danger-500 bg-white text-text-danger-500 hover:bg-text-danger-500/10';
const BTN_DISABLED = 'bg-text-grey';

const formatPrice = (price: number) => new Intl.NumberFormat('id-ID').format(price);

const copyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard?.writeText(value);
  } catch {}
};

/** price = unit price; frontCover null = no image yet */
export type TransactionItemProduct = Pick<BookProduct, 'title' | 'price'> & {
  author: string | null;
  frontCover: string | null;
};

interface CardProductTransactionItemProps {
  product: TransactionItemProduct;
  quantity: number;
  isLast?: boolean;
  className?: string;
}

export const CardProductTransactionItem = ({
  product,
  quantity,
  isLast = false,
  className = '',
}: CardProductTransactionItemProps) => (
  <div className={cn('flex flex-col h-full', className)}>
    <div className="flex items-start gap-3 py-2 h-auto  lg:h-[140px] md:h-[140px] sm:h-[140px]">
      <figure className="aspect-[1/1.15] w-[110px] shrink-0 overflow-hidden rounded-[12px]">
        {product.frontCover && (
          <img
            src={product.frontCover}
            alt={product.title}
            className="block h-full w-full object-cover"
          />
        )}
      </figure>

      <div className="flex min-w-0 min-h-0 flex-1 flex-col gap-1 justify-between h-full">
        <p className="body2 text-text-grey">{product.author}</p>
        <h3 className="line-clamp-2 title1medium text-black">{product.title}</h3>
        <div className="mt-auto flex items-center justify-between gap-2 pt-2 flex-wrap">
          <span className="reg12 text-text-grey">{quantity} Barang</span>
          <span className="title2bold text-black">Rp {formatPrice(product.price * quantity)}</span>
        </div>
      </div>
    </div>
    {!isLast && <div className="h-px w-full bg-blue-cs-200" />}
  </div>
);

const CardProductTransaction = ({
  transaction,
  onPay,
  onCancel,
  onTrack,
  onReceived,
  onRefund,
  onBuyAgain,
  onCopy,
  variant = 'full',
  className = '',
}: CardProductTransactionProps) => {
  const { NEWCOLOR } = useColorStore();
  const {
    invoice,
    date,
    status,
    statusLabel,
    awaitingConfirmation,
    paymentDeadline,
    items,
    totalPayment,
    shipping,
  } = transaction;
  const statusConfig = STATUS_COURIER[status];
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (value: string) => {
    copyToClipboard(value);
    onCopy?.(value, transaction);
    setCopied(value);
    window.setTimeout(() => setCopied((prev) => (prev === value ? null : prev)), 1500);
  };

  const isCompact = variant === 'compact';

  return (
    <article className={cn('w-full flex flex-col gap-3 p-3 shadowCard bg-white', className)}>
      {/* Header: tanggal + status */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 body1medium text-black">
          <Calendar size={18} strokeWidth={1.8} className="text-text-grey" />
          <span>{date}</span>
        </div>
        <span
          className="rounded-[6px] px-2 py-1 reg12 whitespace-nowrap"
          style={{ backgroundColor: NEWCOLOR[statusConfig.bg], color: statusConfig.color }}
        >
          {statusLabel ?? statusConfig.label}
        </span>
      </div>

      {status === 'waiting' && paymentDeadline && (
        <div
          className="w-full p-2 rounded-[8px] reg12 text-[#653E00] inline-flex items-center gap-1"
          style={{ backgroundColor: NEWCOLOR.softDanger }}
        >
          <Clock size={15} />
          <p>Bayar Sebelum {paymentDeadline}</p>
        </div>
      )}

      {/* Invoice */}
      <div className="flex items-center justify-between gap-2 rounded-[8px] bg-[#F3F8FF] px-3 py-2">
        <span className="body1 text-text-grey truncate">{invoice}</span>
        <button
          type="button"
          onClick={() => handleCopy(invoice)}
          aria-label={`Salin nomor invoice ${invoice}`}
          className="flex shrink-0 items-center gap-1 body1medium text-text-blue-100 transition hover:opacity-80"
        >
          <Copy size={14} strokeWidth={1.8} />
          {copied === invoice ? 'Tersalin' : 'Salin'}
        </button>
      </div>

      {/* looping buat card di checout */}
      <div className="flex flex-col">
        {items.map((item, index) => (
          <CardProductTransactionItem
            key={item.product.id}
            product={item.product}
            quantity={item.quantity}
            isLast={index === items.length - 1}
          />
        ))}
      </div>

      {/* Info pengiriman */}
      {!isCompact && shipping && (
        <div className="flex flex-col gap-1 rounded-[8px] border border-[#007AFF1F] bg-[#007AFF1F] p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 body1medium text-black">
              <Truck size={18} strokeWidth={1.8} className="text-text-blue-100" />
              <span>{shipping.courier}</span>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(shipping.trackingNumber)}
              aria-label={`Salin nomor resi ${shipping.trackingNumber}`}
              className="flex shrink-0 items-center gap-1 body1medium text-text-blue-100 transition hover:opacity-80"
            >
              <Copy size={14} strokeWidth={1.8} />
              {copied === shipping.trackingNumber ? 'Tersalin' : shipping.trackingNumber}
            </button>
          </div>
          {shipping.note && <p className="reg12 text-text-grey">{shipping.note}</p>}
        </div>
      )}

      {!isCompact && (
        <>
          <div className="h-px w-full bg-blue-cs-200" />

          <div className="flex items-center flex-row justify-between gap-3 flex-wrap items-start">
            <div className="flex shrink-0 flex-col">
              <span className="reg12 text-text-grey">Total Bayar</span>
              <span className="title2bold text-black">Rp {formatPrice(totalPayment)}</span>
            </div>

            <div className="flex flex-1 items-center justify-end gap-2">
              {/* Menunggu pembayaran: Batalkan + Bayar (nonaktif selama menunggu konfirmasi) */}
              {status === 'waiting' && (
                <>
                  <Button
                    type="button"
                    onClick={() => onCancel?.(transaction)}
                    className={cn(BTN_BASE, BTN_OUTLINE)}
                  >
                    Batalkan
                  </Button>
                  <Button
                    type="button"
                    disabled={awaitingConfirmation}
                    onClick={() => onPay?.(transaction)}
                    className={cn(BTN_BASE, awaitingConfirmation ? BTN_DISABLED : BTN_PRIMARY)}
                  >
                    Bayar
                  </Button>
                </>
              )}

              {/* Diproses: Lacak (menunggu kurir menjemput) + Diterima nonaktif */}
              {status === 'process' && (
                <>
                  <Button
                    type="button"
                    onClick={() => onTrack?.(transaction)}
                    className={cn(BTN_BASE, BTN_OUTLINE)}
                  >
                    <Navigation size={16} strokeWidth={2} />
                    Lacak
                  </Button>
                  <Button type="button" disabled className={cn(BTN_BASE, BTN_DISABLED)}>
                    Diterima
                  </Button>
                </>
              )}

              {/* Dikirim: Lacak + Diterima nonaktif (selesai hanya setelah semua paket sampai) */}
              {status === 'delivery' && (
                <>
                  <Button
                    type="button"
                    onClick={() => onTrack?.(transaction)}
                    className={cn(BTN_BASE, BTN_OUTLINE)}
                  >
                    <Navigation size={16} strokeWidth={2} />
                    Lacak
                  </Button>
                  <Button type="button" disabled className={cn(BTN_BASE, BTN_DISABLED)}>
                    Diterima
                  </Button>
                </>
              )}

              {status === 'come' && (
                <>
                  <Button
                    type="button"
                    onClick={() => onRefund?.(transaction)}
                    className={cn(BTN_BASE, BTN_OUTLINE)}
                  >
                    Ajukan Refund
                  </Button>
                  <Button
                    type="button"
                    onClick={() => onReceived?.(transaction)}
                    className={cn(BTN_BASE, BTN_PRIMARY)}
                  >
                    Diterima
                  </Button>
                </>
              )}

              {/* Selesai / Dibatalkan: Beli Lagi */}
              {(status === 'finish' || status === 'cancel') && (
                <Button
                  type="button"
                  onClick={() => onBuyAgain?.(transaction)}
                  className={cn(BTN_BASE, BTN_PRIMARY, 'w-full')}
                >
                  <RotateCcw size={16} strokeWidth={2} />
                  Beli Lagi
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </article>
  );
};

export default CardProductTransaction;
